import {
  forwardRef,
  Key,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import get from 'lodash/get';

import { usePersistentUiState } from 'utils/customHooks/usePersistentUiState';
import { ChevronDownSvg } from 'components/svgs/ChevronDownSvg';
import { removeHiddenColumnsFromFilters } from 'utils/removeHiddenColumnsFromFilters';
import {
  OpRowMenuItem,
  OpTableRowMenu,
} from 'new-components/OpTableRowMenu/OpTableRowMenu';

import {
  BatchActions,
  CsvExportButton,
  GlobalSearch,
  ShowHideColumnsButton,
  TableHeader,
} from '../OpTableCore/components';
import { OpTableCore } from '../OpTableCore/OpTableCore';
import {
  convertRawColumnsToColumns,
  createFinalColumnKey,
  createTableWidth,
} from '../OpTableCore/helpers';
import { TablePagination } from '../OpTableCore/components/TablePagination';
import { OpButton } from '../OpButton/OpButton';
import { OnFilterUpdateType } from '../OpTableCore/components/ColumnHeaderFilter';
import { OnShowHideColumns } from '../OpTableCore/components/ShowHideColumnsButton';
import type {
  CommonTableProps,
  OpTableRecordType,
  TableState,
} from '../OpTableCore/types';

import {
  getStateToPersist,
  OpTableCorePersistedState,
  opTablePersistedUiVersion,
} from './uiPersistence';

import './OpTable.scss';

export interface OpTableRefType {
  resetRowSelections: () => void;
}

type PaginationProps<RecordType extends OpTableRecordType = OpTableRecordType> =
  CommonTableProps<RecordType>['pagination'] & {
    defaultPageSize?: 10 | 25 | 50 | 100 | 200 | 500;
  };

export type OpTableProps<
  RecordType extends OpTableRecordType = OpTableRecordType,
> = CommonTableProps<RecordType> & {
  rowActions?: (
    record: RecordType,
    dataSourceLength?: number,
  ) => ReactElement[];
  rowMenu?: {
    isVisible?: (record: RecordType) => boolean;
    items: OpRowMenuItem<RecordType>[];
    title: string;
  };
  onRowClick?: (record: RecordType) => void;
  pagination?: PaginationProps<RecordType>;
};

/**
 * OpTable is a comprehensive client-side table component built on Ant Design's Table.
 * It provides advanced features like filtering, sorting, pagination, column management, CSV export,
 * global search, batch actions, and UI state persistence.
 * 
 * Use this component for displaying tabular data with rich interaction capabilities on the client side.
 * For server-side data fetching and pagination, use OpDataFetchTable instead.
 * All filtering, sorting, and pagination are handled client-side for the provided dataSource.
 * 
 * @example
 * <OpTable
 *   dataSource={data}
 *   columns={columns}
 *   rowKey="id"
 *   pagination={{ defaultPageSize: 50 }}
 *   allowGlobalSearch
 *   allowExport
 *   uiStateKey="my-table"
 * />
 * 
 */
const emptyObj: PaginationProps = {};

export const OpTable = forwardRef<OpTableRefType, OpTableProps>(
  (
    {
      dataSource = [],
      columns: rawColumns,
      rowKey = 'id',
      testId = 'op-table',
      height,
      addButton,
      rowActions,
      onRowClick,
      expandable,
      label: tableLabel,
      allowGlobalSearch = true,
      allowExport = true,
      allowShowHideColumns = true,
      gtm,
      pagination: tablePagination = emptyObj,
      batchActionMenuItems,
      uiStateKey,
      loading,
      rowMenu,
      tableHeaderProps,
      ...tableProps
    },
    ref,
  ) => {
    // Ref used to calculate if a row has been clicked, dragged, highlighted, etc
    const rowMouseDownTimestamp = useRef(0);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const {
      uiKey,
      persistedUiState,
      setPersistedUiState,
      canUsePersistedUiState,
      isPersistedUiStateFetching,
      isPersistedUiStateLoading,
      resetPersistedUiState,
    } = usePersistentUiState<OpTableCorePersistedState>({
      uiComponentKey: uiStateKey,
      persistedUiVersion: opTablePersistedUiVersion,
    });

    const resetRowSelections = () => {
      setSelectedRowKeys([]);
    };

    useImperativeHandle(ref, () => {
      return {
        // Allow parent to reset selected rows
        resetRowSelections,
      };
    }, []);

    /** Use rawColumns to determine if any column is filterable. If not, we want
     * to hide the filter portion from all column headers for a cleaner UI */
    const hasHeaderFilter = rawColumns.some(({ filter }) => Boolean(filter));
    /**
     * These updated columns should match columns that would be passed to an antd Table.
     * We want to keep the OpTables props as close to Antd's props as possible and use
     * custom props only when absolutely necessary. See how defaultSortOrder and
     * defaultFilteredValue are used below
     * */
    const defaultStateColumns = useMemo(
      () =>
        convertRawColumnsToColumns({
          rawColumns,
          hasHeaderFilter,
          resetRowSelections,
          gtm,
          isClientSide: true,
        }),
      [gtm, hasHeaderFilter, rawColumns],
    );

    /** Antd will show UI for all columns with defaultSortOrder, but will only respect the
     * first one. Seems more logical to only show the first one in the UI as well as if
     * there are not 2 default sort orders then you are never allowed to sort by more than
     * one column. */
    const columnWithDefaultSort = defaultStateColumns.find(
      (column) => column.defaultSortOrder,
    );

    const defaultState: TableState<(typeof dataSource)[0]> = useMemo(
      () => ({
        default: true,

        // Pagination defaults
        pagination: {
          current: 1,
          pageSize: tablePagination?.defaultPageSize ?? 100,
        },

        // Add a default sorter if there is one
        sorter: {
          column: columnWithDefaultSort,
          order: columnWithDefaultSort?.defaultSortOrder,
          columnKey: columnWithDefaultSort?.key,
          field: columnWithDefaultSort?.key
            ? [columnWithDefaultSort.key]
            : undefined,
        },

        /** Create filters based on how antd creates them. By default any filterable column
         * will have the column key with a value of null and any default values will be added
         * if there are any. */
        filters: rawColumns.reduce(
          (acc, { key, dataIndex, filter, defaultFilteredValue }) => {
            // Add the filter and defaultFilteredValue if it exists
            if (filter && (key || dataIndex)) {
              const finalKey = createFinalColumnKey(key, dataIndex);

              return {
                ...acc,
                [finalKey]: defaultFilteredValue || null,
              };
            }

            return acc;
          },
          {},
        ),

        // Add the columns to state so we can show/hide columns dynamically
        columns: defaultStateColumns,

        // Table global search defaults
        q: '',
      }),
      [columnWithDefaultSort, defaultStateColumns, rawColumns, tablePagination],
    );

    const [state, setState] =
      useState<TableState<(typeof dataSource)[0]>>(defaultState);

    const onFilterUpdate: OnFilterUpdateType = useCallback(
      (filterKey, filterValue) => {
        setState((prevState) => {
          const newState = {
            ...prevState,
            default: false,
            pagination: {
              ...prevState.pagination,
              current: 1, // Reset to page 1 on filter
            },
            filters: {
              ...prevState.filters,
              [`${filterKey}`]: filterValue,
            },
          };
          setPersistedUiState(
            getStateToPersist({
              columns: newState.columns,
              filters: newState.filters,
              sorter: newState.sorter,
            }),
          );

          return newState;
        });
      },
      [setPersistedUiState],
    );

    useEffect(() => {
      if (!isPersistedUiStateFetching) {
        setState((prevState) => {
          const {
            columns: persistedColumns = defaultState.columns,
            filters: persistedFilters = defaultState.filters,
            sorter: persistedSorter = defaultState.sorter,
          } = persistedUiState || {};

          /**
           * check canUsePersistedUiState which tells us if its ok to use the fetched persisted state.
           * The specific scenario this is helpful for is if for some reason a fetch call
           * for our data failed when there is valid data in the db.We don't want to accidentally
           * "reset" the table since persistedState would then be empty and falling back
           * to defaultState
           */
          const finalPrevState = canUsePersistedUiState
            ? {
                ...prevState,
                columns: persistedColumns,
                filters: persistedFilters,
                sorter: persistedSorter,
              }
            : prevState;

          const updatedOpTableColumns = convertRawColumnsToColumns({
            rawColumns,
            hasHeaderFilter,
            resetRowSelections,
            gtm,
            tableState: finalPrevState,
            onFilterUpdate,
            persistedState: persistedUiState,
            shouldUsePersistedColumns: canUsePersistedUiState,
            isClientSide: true,
          });

          return {
            ...prevState,
            columns: updatedOpTableColumns,
            ...(canUsePersistedUiState && {
              default: false,
              filters: persistedFilters,
              sorter: persistedSorter,
            }),
          };
        });
      }
    }, [
      canUsePersistedUiState,
      defaultState.columns,
      defaultState.filters,
      defaultState.sorter,
      gtm,
      hasHeaderFilter,
      isPersistedUiStateFetching,
      onFilterUpdate,
      persistedUiState,
      rawColumns,
    ]);

    // Pagination and filtering are done separately so not included
    const onChange: CommonTableProps<(typeof dataSource)[0]>['onChange'] = (
      _pagination,
      _filters,
      sorter,
    ) => {
      setState((prevState) => {
        const newState = {
          ...prevState,
          default: false,
          sorter,
        };

        setPersistedUiState(
          getStateToPersist({
            columns: newState.columns,
            filters: newState.filters,
            sorter: newState.sorter,
          }),
        );

        return newState;
      });
    };

    /** Width calculated based on the width of each column (default is 180px).
     * We define the width so that we can constrain the table width to the
     * container width and scroll horizontally */
    const tableWidth = createTableWidth(state.columns);

    /** Use state filters to filter the data source (filter returns back a new
     * array so we don't have to worry about modifying the original dataSource) */
    let filteredDataSource = dataSource.filter((record) => {
      let result = true;

      // For global filters
      if (state.q) {
        const filterableColumns = state.columns.filter(
          (column) => column.onFilter,
        );

        if (filterableColumns.length) {
          result = filterableColumns.some((column) =>
            column.onFilter!(state.q.toLowerCase(), record),
          );
        }
      }

      // For column specific filters
      const filtersWithValues = Object.entries(state.filters).filter(
        ([_key, value]) => value && value.length,
      );

      if (filtersWithValues.length) {
        result =
          result &&
          // To support multiple column filters a record must match all filters
          filtersWithValues.every(([key, value]) => {
            const columnOnFilter = state.columns.find(
              (column) => column.key === key,
            )?.onFilter;

            return Array.isArray(value)
              ? value.some((filter) => columnOnFilter!(filter, record))
              : columnOnFilter!(value!, record);
          });
      }

      return result;
    });

    // Set the filtered count for pagination
    const filteredCount = filteredDataSource.length;
    // sorter can optionally be an array, so we need to type narrow
    if (!Array.isArray(state.sorter)) {
      const sorterOrder = state.sorter.order;
      const sorterColumnKey = state.sorter.columnKey
        ? `${state.sorter.columnKey}`
        : undefined;

      // Use state sorter to sort the filtered data
      if (sorterOrder && sorterColumnKey) {
        /** To match the default behavior of the internal table sorter,
         * we allow for a custom sort to be passed */
        if (
          state.sorter.column?.sorter &&
          typeof state.sorter.column.sorter === 'function'
        ) {
          filteredDataSource = filteredDataSource.sort(
            state.sorter.column.sorter,
          );
          if (sorterOrder === 'descend') {
            filteredDataSource.reverse();
          }
        } else {
          const sorted = filteredDataSource.sort((a, b) => {
            // Being safe and checking the value exists

            /* Using get to handle nested dataKeys. Defaulting to empty
           string regardless of type.
           Our number type columns should always be positive integers.
           Empty string should work for both string and number in this case.
           Note that get won't set null values to defaultValue.
           Need to still check for null below */
            let aValue = get(a, sorterColumnKey, '');
            let bValue = get(b, sorterColumnKey, '');

            aValue =
              aValue !== null
                ? typeof aValue === 'string'
                  ? aValue.toLowerCase()
                  : aValue
                : '';

            bValue =
              bValue !== null
                ? typeof bValue === 'string'
                  ? bValue.toLowerCase()
                  : bValue
                : '';

            if (sorterOrder === 'ascend') {
              /**
               * Safeguard potential null values by pushing them to the end
               */
              if (!a) return 1;
              if (!b) return -1;
              if (aValue < bValue) {
                return -1;
              }

              if (aValue > bValue) {
                return 1;
              }

              return 0;
            }

            // Reverse of above
            if (!a) return -1;
            if (!b) return 1;

            // Descend
            if (bValue < aValue) {
              return -1;
            }

            if (bValue > aValue) {
              return 1;
            }

            return 0;
          });

          filteredDataSource = sorted;
        }
      }
    }

    // Use state pagination to paginate the filtered sorted data
    const finalDataSource = filteredDataSource.slice(
      (state.pagination.current - 1) * state.pagination.pageSize,
      state.pagination.current * state.pagination.pageSize,
    );

    const finalColumns = [...state.columns];

    const expandIcon = ({
      expanded,
      onExpand,
      record,
    }: {
      expanded: boolean;
      onExpand(record: OpTableRecordType, e: MouseEvent<HTMLElement>): void;
      record: OpTableRecordType;
    }) => {
      return (
        <OpButton
          className={clsx('op-table__row-expander-button', {
            'op-table__row-expander-button--expanded': expanded,
          })}
          size="small"
          icon={<ChevronDownSvg />}
          onClick={(e) => {
            e.stopPropagation(); // Stop propagation in case the row is editable
            onExpand(record, e);
          }}
          aria-label={expanded ? 'Collapse row' : 'Expand row'}
        />
      );
    };

    // Add an action column if rowActions are passed
    /** This is added as the last thing so that it persists and is
     * not part of the show/hide columns logic */
    if (rowActions) {
      let shouldIncludeWidth = true;

      Object.entries(tableProps).forEach(([propName, propValue]) => {
        /**
         * Explicitly passing scroll as undefined or null to turn off scroll thus we dont need to add a width.
         * This width was needed as the scroll prop adds `table-layout: fixed` which we then seemingly needed this for.
         * We should probably take another look at why this is needed at all
         */
        if (propName === 'scroll' && !propValue) {
          shouldIncludeWidth = false;
        }
      });

      finalColumns.push({
        key: 'op-actions',
        render: (_, record) => rowActions(record),
        ...(shouldIncludeWidth && {
          width: 32 + 32 * rowActions({}).length, // 32 px as a base (16px horizontal padding) and 32 px per action button width looks nice for however many buttons are used.
        }),
        fixed: 'right',
      });
    }

    if (rowMenu) {
      const {
        isVisible = () => true,
        items: rowMenuItems = [],
        title,
      } = rowMenu;

      finalColumns.push({
        className: 'op-table__row-menu-title',
        fixed: 'right',
        title,
        key: 'op-menu',
        render: (_, record) => (
          <OpTableRowMenu
            isVisible={isVisible}
            items={rowMenuItems}
            record={record}
          />
        ),
        width: 75,
      });
    }

    const onShowHideColumns: OnShowHideColumns = (newColumns) => {
      setState((prevState) => {
        const newFilters = removeHiddenColumnsFromFilters(
          newColumns,
          prevState.filters,
        );

        const newState = {
          ...prevState,
          default: false,
          columns: newColumns,
          filters: newFilters,
        };

        setPersistedUiState(
          getStateToPersist({
            columns: newState.columns,
            filters: newState.filters,
            sorter: newState.sorter,
          }),
        );

        return newState;
      });
    };

    const tableTitle = () => (
      <TableHeader
        tableLabel={tableLabel}
        additionalButtons={tableHeaderProps?.additionalButtons}
        batchActions={
          batchActionMenuItems && (
            <BatchActions
              menuItems={batchActionMenuItems}
              selectedRowKeys={selectedRowKeys}
              resetRowSelections={resetRowSelections}
            />
          )
        }
        globalSearch={
          allowGlobalSearch && (
            <GlobalSearch
              gtm={gtm}
              onGlobalSearchChange={({ target: { value } }) => {
                setState((prevState) => ({
                  ...prevState,
                  q: value,
                }));
              }}
            />
          )
        }
        addButton={addButton}
        csvExportButton={
          allowExport && (
            <CsvExportButton
              gtm={gtm}
              testId="csv-export-button"
              // Can transform data with dataMapCallback
              data={filteredDataSource
                .filter((record) => {
                  if (selectedRowKeys.length === 0) {
                    return true;
                  }

                  switch (typeof rowKey) {
                    case 'function': {
                      const key = rowKey(record);
                      return selectedRowKeys.includes(key);
                    }
                    case 'string': {
                      const key = get(record, rowKey);
                      return selectedRowKeys.includes(key);
                    }
                    default:
                      return selectedRowKeys.length === 0;
                  }
                })
                .map(
                  typeof allowExport === 'object'
                    ? allowExport.dataMapCallback || ((record) => record)
                    : (record) => record,
                )}
              // Can filter columns with createColumns
              columns={state.columns.filter(
                typeof allowExport === 'object'
                  ? allowExport.columnsFilterCallback || (() => true)
                  : () => true,
              )}
              filename={
                typeof allowExport === 'object'
                  ? allowExport.filename
                  : undefined
              }
            />
          )
        }
        showHideColumnsButton={
          allowShowHideColumns && (
            <ShowHideColumnsButton
              gtm={gtm}
              tableState={state}
              onShowHideColumns={onShowHideColumns}
              onResetTable={() => {
                setState(defaultState);
                resetPersistedUiState();
              }}
            />
          )
        }
      />
    );

    return (
      <OpTableCore
        key={uiKey}
        uiStateKey={uiStateKey}
        data-testid={testId}
        dataSource={finalDataSource}
        onChange={onChange}
        columns={finalColumns}
        rowKey={rowKey}
        scroll={{
          y: height,
          x: tableWidth,
        }}
        gtm={gtm}
        pagination={false} // We are using custom pagination
        {...(tablePagination && {
          // eslint-disable-next-line react/no-unstable-nested-components
          footer: () => (
            <TablePagination
              currentPage={state.pagination.current}
              pageSize={state.pagination.pageSize}
              filteredCount={filteredCount}
              totalCount={dataSource.length}
              setTableState={setState}
            />
          ),
        })}
        // Allow row selection if batchActionOptions passed
        {...(batchActionMenuItems && {
          rowSelection: {
            selectedRowKeys,
            onChange: (newSelectedRowKeys) =>
              setSelectedRowKeys(newSelectedRowKeys),
            preserveSelectedRowKeys: true,
          },
        })}
        // Only show table title if there is content to show
        {...((tableLabel ||
          allowGlobalSearch ||
          allowExport ||
          allowShowHideColumns) && {
          title: tableTitle,
        })}
        // Allow row to be clicked to edit
        onRow={(record) => {
          return {
            className: clsx({
              'ant-table-row--clickable': Boolean(onRowClick),
            }),
            onMouseDown: () => {
              rowMouseDownTimestamp.current = Date.now();
            },
            onClick: () => {
              /**
               * If user clicks a row and doesn't immediately trigger a mouseup
               * don't invoke any of the rowAction callbacks
               */
              if (Date.now() - rowMouseDownTimestamp.current > 150) {
                return;
              }

              if (onRowClick) {
                onRowClick(record);
              }
            },
            'data-gtm': `${gtm}-row-click`,
          };
        }}
        expandable={
          expandable && {
            ...expandable,
            expandIcon,
          }
        }
        loading={isPersistedUiStateLoading || loading}
        {...tableProps}
      />
    );
  },
) as <RecordType extends OpTableRecordType>(
  props: OpTableProps<RecordType> & RefAttributes<OpTableRefType>,
) => ReactNode; // Casting here is necessary since there's no real way to create generics forwardRefs
